import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatWarrantyDetail, isUnderWarranty } from '@fixflow/shared-types';
import { portalApi } from '@/lib/api/portal.js';
import { formatCurrency, formatDateTime } from '@/lib/format.js';
import { PortalProgress } from '@/components/portal/PortalProgress.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

type TicketDetail = {
  ticketNumber: string;
  status: string;
  statusLabel: string;
  statusDescription: string;
  progressIndex: number;
  promisedDate: string | null;
  customerComplaint?: string | null;
  devices: Array<{ summary: string; condition?: string }>;
  repairs: Array<{ serviceName: string; price: string; status: string }>;
  notes: Array<{ id: string; body: string; createdAt: string }>;
  subtotal: string;
  tax: string;
  total: string;
  paidTotal?: string;
  depositPaid: string;
  balanceDue: string;
  payments?: Array<{
    id: string;
    amount: string;
    tipAmount?: string;
    method: string;
    isDeposit?: boolean;
    createdAt: string;
  }>;
  warrantyType?: 'days' | 'lifetime' | 'none' | null;
  warrantyDays?: number | null;
  warrantyExpiresAt?: string | null;
  storeInfo?: { name: string; phone: string | null; address: string } | null;
  m360Summary?: { preGrade: string | null; postGrade: string | null } | null;
};

export function PortalTicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');

  const ticketQuery = useQuery({
    queryKey: ['portal', 'ticket', id],
    queryFn: () => portalApi.ticket(id!) as Promise<TicketDetail>,
    enabled: Boolean(id),
  });

  const noteMutation = useMutation({
    mutationFn: () => portalApi.addNote(id!, note),
    onSuccess: () => {
      setNote('');
      queryClient.invalidateQueries({ queryKey: ['portal', 'ticket', id] });
      toastSuccess('Note sent to the shop');
    },
    onError: (err) => {
      const parsed = parseApiError(err);
      toastError(parsed.message, parsed.code);
    },
  });

  const t = ticketQuery.data;

  if (ticketQuery.isLoading) {
    return <div className="rounded-2xl bg-white p-8 text-slate-500 shadow-sm">Loading…</div>;
  }
  if (!t) {
    return <div className="rounded-2xl bg-white p-8 text-slate-500 shadow-sm">Ticket not found.</div>;
  }

  const device = t.devices?.[0];

  return (
    <div className="space-y-5">
      <Link to="/portal/tickets" className="text-sm font-semibold text-slate-500 hover:text-slate-800">
        ← Back to repairs
      </Link>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-slate-950">
              {t.ticketNumber} · {device?.summary ?? 'Device'}
            </h1>
            <p className="mt-1 text-sm text-slate-500">{t.statusDescription}</p>
          </div>
          <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-800">
            ● {t.statusLabel}
          </span>
        </div>
        <div className="mt-4">
          <PortalProgress index={t.progressIndex} />
        </div>
      </div>

      {t.m360Summary ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-700">
            Your Device Health Check
          </h2>
          <div className="mt-3 space-y-1 text-sm text-slate-800">
            {t.m360Summary.preGrade ? (
              <p>
                Before repair: <span className="font-semibold">Grade {t.m360Summary.preGrade}</span>
              </p>
            ) : null}
            {t.m360Summary.postGrade ? (
              <p>
                After repair:{' '}
                <span className="font-semibold">Grade {t.m360Summary.postGrade}</span>
                {t.m360Summary.preGrade &&
                t.m360Summary.postGrade < t.m360Summary.preGrade
                  ? ' ↑'
                  : ''}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">Repair Info</h2>
        {t.customerComplaint?.trim() ? (
          <div className="mt-3 rounded-xl bg-slate-50 p-3">
            <h3 className="text-xs font-bold uppercase text-slate-500">Your issue</h3>
            <p className="mt-1 whitespace-pre-wrap text-sm text-slate-800">{t.customerComplaint.trim()}</p>
          </div>
        ) : null}
        <p className="mt-2 text-sm">
          Device: {device?.summary}
          {device?.condition ? ` (${device.condition} condition)` : ''}
        </p>
        <ul className="mt-2 space-y-1 text-sm text-slate-700">
          {t.repairs?.map((r) => (
            <li key={r.serviceName}>
              {r.serviceName} — {formatCurrency(r.price)}
            </li>
          ))}
        </ul>
        {t.notes?.length ? (
          <div className="mt-4 space-y-2">
            <h3 className="text-xs font-bold uppercase text-slate-500">Store notes</h3>
            {t.notes.map((n) => (
              <p key={n.id} className="rounded-xl bg-slate-50 p-3 text-sm text-slate-700">
                {n.body}
                <span className="mt-1 block text-xs text-slate-400">{formatDateTime(n.createdAt)}</span>
              </p>
            ))}
          </div>
        ) : null}
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">Billing</h2>
        <dl className="mt-3 space-y-1.5 text-sm">
          {t.repairs?.map((r) => (
            <div key={r.serviceName} className="flex justify-between">
              <dt>{r.serviceName}</dt>
              <dd>{formatCurrency(r.price)}</dd>
            </div>
          ))}
          <div className="flex justify-between text-slate-500">
            <dt>Tax</dt>
            <dd>{formatCurrency(t.tax)}</dd>
          </div>
          <div className="flex justify-between border-t border-slate-100 pt-2 font-bold">
            <dt>Total</dt>
            <dd>{formatCurrency(t.total)}</dd>
          </div>
          {(t.payments ?? []).length > 0 ? (
            <div className="border-t border-slate-100 pt-2">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                Payments received
              </p>
              {(t.payments ?? []).map((p) => (
                <div key={p.id} className="space-y-0.5">
                  <div className="flex justify-between text-slate-500">
                    <dt>
                      {p.isDeposit ? 'Deposit' : 'Payment'} · {p.method.replaceAll('_', ' ')} ·{' '}
                      {formatDateTime(p.createdAt)}
                    </dt>
                    <dd>
                      -
                      {formatCurrency(
                        Number(p.amount) + (p.tipAmount ? Number(p.tipAmount) : 0),
                      )}
                    </dd>
                  </div>
                  {p.tipAmount && Number(p.tipAmount) > 0 ? (
                    <div className="flex justify-between pl-2 text-xs text-slate-400">
                      <dt>Tip</dt>
                      <dd>{formatCurrency(p.tipAmount)}</dd>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : Number(t.depositPaid ?? t.paidTotal ?? 0) > 0 ? (
            <div className="flex justify-between text-slate-500">
              <dt>Paid to date</dt>
              <dd>-{formatCurrency(t.depositPaid ?? t.paidTotal)}</dd>
            </div>
          ) : null}
          <div className="flex justify-between font-semibold text-[#1B4F8A]">
            <dt>{Number(t.balanceDue) < 0 ? 'Credit' : 'Balance Due'}</dt>
            <dd>
              {Number(t.balanceDue) < 0
                ? formatCurrency(Math.abs(Number(t.balanceDue)))
                : Number(t.balanceDue) <= 0
                  ? 'Paid in full'
                  : formatCurrency(t.balanceDue)}
            </dd>
          </div>
        </dl>
        <button
          type="button"
          disabled
          className="mt-4 w-full rounded-xl bg-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-400"
          title="Payment processing coming soon"
        >
          💳 Pay Balance
        </button>
        <p className="mt-1 text-center text-xs text-slate-400">Payment processing coming soon</p>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">Add a Note</h2>
        <textarea
          className="mt-3 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
          rows={3}
          placeholder="Have a question? Leave a note for the shop…"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <button
          type="button"
          disabled={!note.trim() || noteMutation.isPending}
          onClick={() => noteMutation.mutate()}
          className="mt-2 rounded-xl bg-[#1B4F8A] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          Send Note
        </button>
      </section>

      {t.status === 'closed' &&
      isUnderWarranty({
        type: t.warrantyType,
        days: t.warrantyDays,
        expiresAt: t.warrantyExpiresAt,
      }) ? (
        <section className="rounded-2xl border border-green-200 bg-green-50 p-5">
          <h2 className="font-bold text-green-900">
            ✓{' '}
            {formatWarrantyDetail({
              type: t.warrantyType,
              days: t.warrantyDays,
              expiresAt: t.warrantyExpiresAt,
              formatDate: (d) => formatDateTime(d.toISOString()),
            })}
          </h2>
          {t.warrantyType !== 'lifetime' && t.warrantyExpiresAt ? (
            <p className="mt-1 text-sm text-green-800">
              Expires: {formatDateTime(t.warrantyExpiresAt)}
            </p>
          ) : null}
          <p className="mt-2 text-sm text-green-700">
            If you experience any issues with this repair within the warranty period, contact us.
          </p>
        </section>
      ) : null}

      {t.storeInfo ? (
        <p className="text-center text-sm text-slate-500">
          Questions? Call {t.storeInfo.phone ?? t.storeInfo.name}
          {t.storeInfo.address ? ` — ${t.storeInfo.address}` : ''}
        </p>
      ) : null}
    </div>
  );
}
